// src/components/crm/ActivityFilters.jsx
import React from "react";
import {
  HiOutlineSearch,
  HiChevronDown,
  HiChevronUp,
  HiCheck,
} from "react-icons/hi";

import {
  HiOutlineAdjustmentsHorizontal,
  HiArrowPath,
  HiXMark,
} from "react-icons/hi2";

/** Utils */
function useDebouncedValue(value, delay = 400) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}
const ACTION_OPTIONS = [
  "CARD_CREATED",
  "CARD_UPDATED",
  "CARD_DELETED",
  "TASK_CREATED",
  "TASK_UPDATED",
  "TASK_DONE",
  "COMMENT_ADDED",
  "STAGE_MOVED",
  "STATUS_CHANGED",
  "LINKED",
  "FUNNEL_UPDATED",
];

export default function ActivityFilters({ value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [local, setLocal] = React.useState(() => ({
    q: value?.q || "",
    actions: Array.isArray(value?.actions) ? value.actions : [],
    actorUserId: value?.actorUserId || "",
    targetType: value?.targetType || "",
    targetId: value?.targetId || "",
    dateFrom: value?.dateFrom || "",
    dateTo: value?.dateTo || "",
  }));

  // Quando value externo mudar (ex: reset pelo parent), sincroniza
  React.useEffect(() => {
    setLocal((s) => ({
      ...s,
      q: value?.q || "",
      actions: Array.isArray(value?.actions) ? value.actions : [],
      actorUserId: value?.actorUserId || "",
      targetType: value?.targetType || "",
      targetId: value?.targetId || "",
      dateFrom: value?.dateFrom || "",
      dateTo: value?.dateTo || "",
    }));
  }, [value]);

  const debouncedQ = useDebouncedValue(local.q, 450);
  // Notifica parent apenas quando q debounced muda
  React.useEffect(() => {
    onChange?.({ ...value, q: debouncedQ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  function applyAdvanced() {
    onChange?.({
      ...value,
      actions: local.actions,
      actorUserId: local.actorUserId,
      targetType: local.targetType,
      targetId: local.targetId,
      dateFrom: local.dateFrom,
      dateTo: local.dateTo,
    });
  }
  function quickRefresh() {
    onChange?.({ ...value }); // dispara refetch mantendo filtros
  }
  function clearAll() {
    const cleared = {
      q: "",
      actions: [],
      actorUserId: "",
      targetType: "",
      targetId: "",
      dateFrom: "",
      dateTo: "",
    };
    setLocal(cleared);
    onChange?.(cleared);
  }

  // “Pílulas” de filtros ativos
  const activeChips = React.useMemo(() => {
    const chips = [];
    if (value?.q) chips.push({ key: "q", label: `Busca: "${value.q}"` });
    if (value?.actions?.length)
      chips.push({
        key: "actions",
        label: `Ações: ${value.actions.length}`,
      });
    if (value?.actorUserId)
      chips.push({
        key: "actorUserId",
        label: `Usuário: ${value.actorUserId}`,
      });
    if (value?.targetType)
      chips.push({
        key: "targetType",
        label: `Alvo: ${value.targetType}${
          value?.targetId ? ` #${value.targetId}` : ""
        }`,
      });
    if (value?.dateFrom || value?.dateTo)
      chips.push({
        key: "date",
        label: `Período: ${value.dateFrom || "—"} → ${value.dateTo || "—"}`,
      });
    return chips;
  }, [value]);

  function removeChip(key) {
    const next = { ...value };
    if (key === "q") next.q = "";
    if (key === "actions") next.actions = [];
    if (key === "actorUserId") next.actorUserId = "";
    if (key === "targetType") {
      next.targetType = "";
      next.targetId = "";
    }
    if (key === "date") {
      next.dateFrom = "";
      next.dateTo = "";
    }
    setLocal((s) => ({ ...s, ...next }));
    onChange?.(next);
  }

  return (
    <div className="mb-4">
      {/* Barra principal */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="relative flex-1">
          <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            value={local.q}
            onChange={(e) => setLocal((s) => ({ ...s, q: e.target.value }))}
            type="text"
            placeholder="Buscar por texto (ex: título, nota, e-mail)…"
            className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-gray-50"
            aria-expanded={open}
          >
            <HiOutlineAdjustmentsHorizontal className="h-5 w-5" />
            Filtros avançados
            {open ? (
              <HiChevronUp className="h-4 w-4 opacity-70" />
            ) : (
              <HiChevronDown className="h-4 w-4 opacity-70" />
            )}
          </button>

          <button
            type="button"
            onClick={quickRefresh}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-gray-50"
            title="Recarregar resultados"
          >
            <HiArrowPath className="h-5 w-5" />
            Atualizar
          </button>

          <button
            type="button"
            onClick={clearAll}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-gray-50"
            title="Limpar todos os filtros"
          >
            <HiXMark className="h-5 w-5" />
            Limpar
          </button>
        </div>
      </div>

      {/* Chips */}
      {activeChips.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {activeChips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 text-sm"
            >
              {c.label}
              <button
                onClick={() => removeChip(c.key)}
                className="hover:bg-indigo-100 rounded-full p-0.5"
                title="Remover filtro"
              >
                <HiXMark className="h-4 w-4" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Painel avançado */}
      {open && (
        <div className="mt-3 p-3 sm:p-4 rounded-xl border bg-white shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            {/* Ações (multi-select) */}
            <div className="md:col-span-5">
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                Ações
              </label>
              <MultiSelect
                options={ACTION_OPTIONS}
                values={local.actions}
                onChange={(vals) =>
                  setLocal((s) => ({ ...s, actions: vals || [] }))
                }
                placeholder="Selecione ações…"
              />
            </div>

            {/* Usuário */}
            <div className="md:col-span-3">
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                Usuário (ID)
              </label>
              <input
                value={local.actorUserId}
                onChange={(e) =>
                  setLocal((s) => ({ ...s, actorUserId: e.target.value }))
                }
                inputMode="numeric"
                placeholder="ex: 42"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
              />
            </div>

            {/* Tipo de Alvo */}
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                Tipo de Alvo
              </label>
              <select
                value={local.targetType}
                onChange={(e) =>
                  setLocal((s) => ({ ...s, targetType: e.target.value }))
                }
                className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
              >
                <option value="">—</option>
                <option value="CARD">Card</option>
                <option value="LEAD">Lead</option>
                <option value="TASK">Task</option>
                <option value="FUNNEL">Funnel</option>
                <option value="STAGE">Stage</option>
                <option value="COMPANY">Company</option>
                <option value="USER">User</option>
              </select>
            </div>

            {/* ID do Alvo */}
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                ID do Alvo
              </label>
              <input
                value={local.targetId}
                onChange={(e) =>
                  setLocal((s) => ({ ...s, targetId: e.target.value }))
                }
                placeholder="ex: 1001"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
              />
            </div>

            {/* Datas */}
            <div className="md:col-span-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">
                  De
                </label>
                <input
                  type="date"
                  value={local.dateFrom}
                  onChange={(e) =>
                    setLocal((s) => ({ ...s, dateFrom: e.target.value }))
                  }
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">
                  Até
                </label>
                <input
                  type="date"
                  value={local.dateTo}
                  onChange={(e) =>
                    setLocal((s) => ({ ...s, dateTo: e.target.value }))
                  }
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={clearAll}
              className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50"
            >
              Limpar
            </button>
            <button
              type="button"
              onClick={applyAdvanced}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
            >
              <HiCheck className="h-5 w-5" />
              Aplicar filtros
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** ---------- MultiSelect simples com checkboxes ---------- */
function MultiSelect({ options = [], values = [], onChange, placeholder }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function toggle(val) {
    const set = new Set(values);
    if (set.has(val)) set.delete(val);
    else set.add(val);
    onChange?.([...set]);
  }
  function clear() {
    onChange?.([]);
  }

  const label =
    values.length === 0
      ? placeholder || "Selecionar…"
      : `${values.length} selecionada(s)`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-3 py-2 rounded-lg border bg-white hover:bg-gray-50"
      >
        <div className="flex items-center justify-between">
          <span className="truncate">{label}</span>
          {open ? (
            <HiChevronUp className="h-4 w-4 opacity-60" />
          ) : (
            <HiChevronDown className="h-4 w-4 opacity-60" />
          )}
        </div>
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded-lg border bg-white shadow-lg">
          <div className="p-2 border-b flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {values.length} selecionada(s)
            </span>
            <button
              type="button"
              onClick={clear}
              className="text-xs text-gray-600 hover:text-gray-900"
            >
              Limpar
            </button>
          </div>
          <ul className="p-1">
            {options.map((opt) => {
              const checked = values.includes(opt);
              return (
                <li key={opt}>
                  <label className="flex items-center gap-2 px-2 py-2 rounded hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(opt)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">{opt}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
